import {
  IFlattenedSearchProductsRequest,
  ISuggestionEntry,
  ISuggestionSearchDataType,
} from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/Interfaces";
import { BRAND_VALUES } from "@faire/retailer-visitor-shared/consts/BRAND_VALUES";
import { decodeMultimodalQueryText } from "@faire/retailer-visitor-shared/features/MultimodalProductSearch/multimodalQuery";
import { localizedUrlWithQueryParams } from "@faire/retailer-visitor-shared/lib/pathUtils";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { ISearchProductsRequest } from "@faire/web-api--source/indigofair/data/ISearchProductsRequest";
import { ISearchRequestReferrer } from "@faire/web-api--source/indigofair/data/ISearchRequestReferrer";
import productSearch from "@faire/web-api--source/routes/www/search";

/**
 * Build the query with appropriate filters for use in productSearch() later
 * @param {IFlattenedSearchProductsRequest} queryValue  The original SearchProductsRequest
 * @param {string}                          refReqId    The request ID for tracking purposes
 * @param {string|undefined}                filters     The filters to be applied in the query
 * @returns {string}                                    A string URL
 */
const buildLocalizedUrlWithQueryParams = (
  queryValue: IFlattenedSearchProductsRequest,
  refReqId: string | undefined,
  filters: string | undefined,
  strictLocalize: StrictLocalizeFunction
): string => {
  return localizedUrlWithQueryParams(
    productSearch({
      q: queryValue.query ?? "",
      // TODO(cedo): Clean this up. I think this is legacy code now.
      // We now use "filters" query param for filtering the search, but
      // we also don't preserve filters when searching with a new query.
      filter: Object.keys(BRAND_VALUES(strictLocalize)).reduce<
        string | undefined
      >(
        // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
        (result, value) => (queryValue[value] ? value : result),
        undefined
      ),
      filters,
    }),
    {
      refType:
        ISearchRequestReferrer.SearchRequestReferrerType
          .SUGGESTIONS_SEARCH_QUERIES,
      refReqId,
    }
  );
};

/**
 * Construct an ISuggestionEntry[] for suggested search queries
 * @param   {ISearchProductsRequest} queryObject Object with search suggestions
 * @param   {string}                 sectionName Name of the section
 * @returns {ISuggestionEntry}                   An array of processed search suggestions
 */
export const buildQueriesSuggestionArray = (
  queryObject: Partial<Record<string, ISearchProductsRequest>>,
  sectionName: string,
  requestID: string | undefined,
  maxNumberOfQuerySuggestions: number,
  strictLocalize: StrictLocalizeFunction
): ISuggestionEntry<string | Partial<ISuggestionSearchDataType>>[] => {
  if (!queryObject || Object.keys(queryObject).length === 0) {
    return [];
  }

  const queryObjectKeys: string[] = Object.keys(queryObject);
  const queryObjectValues: ISearchProductsRequest[] = Object.values(
    queryObject
  ) as ISearchProductsRequest[];

  // Flatten queryObjectValues
  const searchProductsRequests: IFlattenedSearchProductsRequest[] =
    queryObjectValues
      .flatMap(({ query, categories, autocomplete_categories }, index) => {
        if (autocomplete_categories?.length) {
          // Make an instance of IFlattenedSearchProductsRequest for every category found in autocomplete_categories
          return autocomplete_categories.map((autocomplete_category) => ({
            queryKey: queryObjectKeys[index],
            query,
            categories,
            ...autocomplete_category,
          }));
        }

        // If autocomplete_categories is empty, return instance of
        // IFlattenedSearchProductsRequest with undefined category_display_name and category_key
        return {
          queryKey: queryObjectKeys[index],
          query,
          categories,
          autocomplete_categories,
          category_display_name: undefined,
          category_key: undefined,
        };
      })
      .slice(0, maxNumberOfQuerySuggestions);

  const queryData = [];
  for (const suggestion of searchProductsRequests) {
    // Can't build a ISuggestionEntry if query doesn't exist
    if (!suggestion.query) {
      continue;
    }

    // Create a new ISuggestionEntry for each IFlattenedSearchProductsRequest
    queryData.push(
      ISuggestionEntry.build({
        label: decodeMultimodalQueryText(suggestion.queryKey),
        url: buildLocalizedUrlWithQueryParams(
          suggestion,
          requestID,
          suggestion.category_key,
          strictLocalize
        ),
        sectionName,
        searchData: suggestion,
        requestID,
      })
    );
  }

  return queryData;
};
